import {
  buildJsonLd,
  OG_IMAGE,
  resolveRouteSeo as resolveRouteSeoConfig,
  SITE_ORIGIN,
  TWITTER_HANDLE,
  truncateSeoDescription,
  truncateSeoTitle,
  type RouteSeo,
} from "@/lib/seoConfig";

export { SITE_ORIGIN, truncateSeoDescription, truncateSeoTitle };

/**
 * Título visible en la pestaña / tooltip del navegador. SIEMPRE "Popistant".
 * El título rico (keywords) sigue vivo en el HTML prerenderizado que ve Google
 * y en og:title / JSON-LD; aquí solo controlamos lo que ve el usuario.
 */
const VISIBLE_TITLE = "Popistant";

export type DocumentSeo = {
  title: string;
  description: string;
  canonical: string;
  robots?: string;
  ogImage?: string;
  /** Opcionales: presentes cuando viene de resolveRouteSeo. */
  pageType?: RouteSeo["pageType"];
  h1?: string;
  intro?: string;
  pageTitle?: string;
};

function setMetaBy(attr: "name" | "property", key: string, content: string) {
  const selector = attr === "name" ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Inserta/actualiza el JSON-LD principal al navegar (SPA). */
function setJsonLd(seo: DocumentSeo) {
  if (!seo.canonical) return;
  const json = buildJsonLd({
    pageTitle: seo.pageTitle ?? seo.title,
    title: seo.title,
    description: seo.description,
    canonical: seo.canonical,
    h1: seo.h1 ?? seo.title,
    intro: seo.intro ?? seo.description,
    robots: seo.robots,
    pageType: seo.pageType ?? "WebPage",
  });
  let el = document.querySelector('script[type="application/ld+json"]#seo-jsonld') as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "seo-jsonld";
    document.head.appendChild(el);
  }
  el.textContent = json;
}

export function setDocumentSeo(opts: DocumentSeo): void {
  if (typeof document === "undefined") return;

  // El usuario solo debe leer "Popistant" en la pestaña / tooltip, en todas
  // las páginas. El título rico para SEO vive en og:title, JSON-LD y en el
  // HTML prerenderizado que recibe Google.
  document.title = VISIBLE_TITLE;

  let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (!metaDesc) {
    metaDesc = document.createElement("meta");
    metaDesc.setAttribute("name", "description");
    document.head.appendChild(metaDesc);
  }
  metaDesc.setAttribute("content", opts.description);

  const ogImage = opts.ogImage ?? OG_IMAGE;
  setMetaBy("property", "og:title", opts.title);
  setMetaBy("property", "og:description", opts.description);
  setMetaBy("property", "og:url", opts.canonical);
  setMetaBy("property", "og:image", ogImage);
  setMetaBy("property", "og:type", "website");
  setMetaBy("property", "og:site_name", "Popistant");
  setMetaBy("property", "og:locale", "es_ES");

  setMetaBy("name", "twitter:card", "summary_large_image");
  setMetaBy("name", "twitter:title", opts.title);
  setMetaBy("name", "twitter:description", opts.description);
  setMetaBy("name", "twitter:image", ogImage);
  setMetaBy("name", "twitter:site", TWITTER_HANDLE);

  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", opts.canonical);

  if (opts.robots) {
    setMetaBy("name", "robots", opts.robots);
  } else {
    setMetaBy("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1");
  }

  setJsonLd(opts);
}

/** SEO por ruta (delegado a la fuente única seoConfig). */
export function resolveRouteSeo(pathname: string, search: string): DocumentSeo {
  return resolveRouteSeoConfig(pathname, search);
}
